import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { database } from '@/utils/database';

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: 'entity' | 'brand' | 'document' | 'page' | 'action';
  icon: string;
  url?: string;
  action?: () => void;
  metadata?: Record<string, unknown>;
}

export interface SearchGroup {
  label: string;
  results: SearchResult[];
}

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchGroup[];
  isSearching: boolean;
  recentSearches: string[];
  clearRecentSearches: () => void;
  addToRecentSearches: (query: string) => void;
}

const RECENT_SEARCHES_KEY = 'legalsyncup_recent_searches';
const MAX_RECENT_SEARCHES = 5;

// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useGlobalSearch(): UseGlobalSearchReturn {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<SearchGroup[]>([]);

  const debouncedQuery = useDebounce(query, 300);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load recent searches:', error);
    }
  }, []);

  // Add to recent searches
  const addToRecentSearches = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setRecentSearches((prev) => {
      const updated = [searchQuery, ...prev.filter((q) => q !== searchQuery)].slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save recent searches:', error);
      }
      return updated;
    });
  }, []);

  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
      console.error('Failed to clear recent searches:', error);
    }
  }, []);

  // Static page results
  const pageResults = useMemo((): SearchResult[] => {
    const pages = [
      { id: 'dashboard', title: 'Dashboard', url: '/dashboard', icon: 'LayoutDashboard' },
      { id: 'entities', title: 'Swiss Entities', url: '/entities', icon: 'Building2' },
      { id: 'documents', title: 'Documents', url: '/documents', icon: 'FileText' },
      { id: 'workflows', title: 'Approval Workflows', url: '/workflows', icon: 'GitBranch' },
      { id: 'settings', title: 'Settings', url: '/settings', icon: 'Settings' },
      { id: 'team', title: 'Team Management', url: '/team', icon: 'Users' },
    ];

    return pages.map((page) => ({
      ...page,
      type: 'page' as const,
      subtitle: 'Navigate to page',
    }));
  }, []);

  // Action results
  const actionResults = useMemo((): SearchResult[] => {
    return [
      {
        id: 'create-entity',
        title: 'Create New Entity',
        subtitle: 'Add a new Swiss legal entity',
        type: 'action',
        icon: 'Plus',
        action: () => navigate('/entities/new'),
      },
      {
        id: 'create-document',
        title: 'Create New Document',
        subtitle: 'Start a new legal document',
        type: 'action',
        icon: 'FilePlus',
        action: () => navigate('/documents/new'),
      },
      {
        id: 'create-workflow',
        title: 'Create Approval Workflow',
        subtitle: 'Set up a new approval process',
        type: 'action',
        icon: 'GitBranch',
        action: () => navigate('/workflows/new'),
      },
    ];
  }, [navigate]);

  // Perform search
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedQuery.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      try {
        const searchTerm = debouncedQuery.toLowerCase();
        const groups: SearchGroup[] = [];

        // Search entities
        const { data: entities } = await database.entities.listEntities();
        if (entities && entities.length > 0) {
          const entityResults = entities
            .filter(
              (entity) =>
                entity.name.toLowerCase().includes(searchTerm) ||
                entity.uid.toLowerCase().includes(searchTerm) ||
                entity.legal_form.toLowerCase().includes(searchTerm)
            )
            .slice(0, 5)
            .map((entity) => ({
              id: entity.id,
              title: entity.name,
              subtitle: `${entity.uid} - ${entity.legal_form}`,
              type: 'entity' as const,
              icon: 'Building2',
              url: `/entities/${entity.id}`,
              metadata: { uid: entity.uid, status: entity.status },
            }));

          if (entityResults.length > 0) {
            groups.push({ label: 'Swiss Entities', results: entityResults });
          }
        }

        // Search brands
        const { data: brands } = await database.brands.listBrands();
        if (brands && brands.length > 0) {
          const brandResults = brands
            .filter(
              (brand) =>
                brand.name.toLowerCase().includes(searchTerm) ||
                (brand.description && brand.description.toLowerCase().includes(searchTerm))
            )
            .slice(0, 5)
            .map((brand) => ({
              id: brand.id,
              title: brand.name,
              subtitle: brand.description || 'Brand',
              type: 'brand' as const,
              icon: 'Tag',
              url: `/brands/${brand.id}`,
            }));

          if (brandResults.length > 0) {
            groups.push({ label: 'Brands', results: brandResults });
          }
        }

        // Search documents
        const { data: documents } = await database.documents.listDocuments();
        if (documents && documents.length > 0) {
          const documentResults = documents
            .filter(
              (doc) =>
                doc.title.toLowerCase().includes(searchTerm) ||
                doc.type.toLowerCase().includes(searchTerm)
            )
            .slice(0, 5)
            .map((doc) => ({
              id: doc.id,
              title: doc.title,
              subtitle: `${doc.type} - ${doc.language}`,
              type: 'document' as const,
              icon: 'FileText',
              url: `/documents/${doc.id}`,
              metadata: { type: doc.type, status: doc.approval_status },
            }));

          if (documentResults.length > 0) {
            groups.push({ label: 'Documents', results: documentResults });
          }
        }

        // Search pages
        const filteredPages = pageResults.filter(
          (page) =>
            page.title.toLowerCase().includes(searchTerm) ||
            (page.subtitle && page.subtitle.toLowerCase().includes(searchTerm))
        );
        if (filteredPages.length > 0) {
          groups.push({ label: 'Pages', results: filteredPages });
        }

        // Search actions
        const filteredActions = actionResults.filter(
          (action) =>
            action.title.toLowerCase().includes(searchTerm) ||
            (action.subtitle && action.subtitle.toLowerCase().includes(searchTerm))
        );
        if (filteredActions.length > 0) {
          groups.push({ label: 'Actions', results: filteredActions });
        }

        setSearchResults(groups);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedQuery, pageResults, actionResults]);

  // Return recent searches when query is empty
  const results = useMemo(() => {
    if (!query.trim() && recentSearches.length > 0) {
      return [
        {
          label: 'Recent Searches',
          results: recentSearches.slice(0, 5).map((search, index) => ({
            id: `recent-${index}`,
            title: search,
            type: 'action' as const,
            icon: 'Clock',
            action: () => setQuery(search),
          })),
        },
      ];
    }
    return searchResults;
  }, [query, searchResults, recentSearches]);

  return {
    query,
    setQuery,
    results,
    isSearching,
    recentSearches,
    clearRecentSearches,
    addToRecentSearches,
  };
}
